/**
 * Der freie Zeitraumkalender um einen Auslöser: am Desktop als Popover im Portal (der Auslöser
 * sitzt oft in einem scrollenden Bereich, der ihn sonst abschnitte), am Handy als eigenes Blatt –
 * im Bearbeiten-Blatt hätte ein Popover keinen Platz (FRONTEND_GUIDELINES §8).
 */
import * as Popover from '@radix-ui/react-popover';
import { Slot } from '@radix-ui/react-slot';
import { useState, type ReactNode } from 'react';
import { BottomSheet } from '@/shared/components';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { RangeCalendar } from './RangeCalendar';
import type { IsoRange } from './rangePickerModel';

interface RangePickerPopoverProps {
  today: string;
  initial?: IsoRange;
  applyLabel: string;
  title: string;
  children: ReactNode;
  onApply: (range: IsoRange) => void;
}

export function RangePickerPopover({ title, children, ...props }: RangePickerPopoverProps) {
  const [open, setOpen] = useState(false);
  const mobile = useMediaQuery('(max-width: 767px)');
  const calendar = open && (
    <RangeCalendar
      today={props.today}
      initial={props.initial}
      applyLabel={props.applyLabel}
      onCancel={() => setOpen(false)}
      onApply={(range) => {
        props.onApply(range);
        setOpen(false);
      }}
    />
  );
  if (mobile) {
    return (
      <>
        <Slot onClick={() => setOpen(true)}>{children}</Slot>
        <BottomSheet open={open} onOpenChange={setOpen} title={title}>
          <div className="px-4 pb-4">{calendar}</div>
        </BottomSheet>
      </>
    );
  }
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          collisionPadding={16}
          aria-label={title}
          className="bg-card border-hairline shadow-panel rounded-tile z-50 max-h-[85dvh] w-[min(640px,calc(100vw-32px))] overflow-y-auto border p-5"
        >
          <p className="text-ink mb-3 font-serif text-base font-bold">{title}</p>
          {calendar}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
